import type { ReactNode } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Link, Navigate, useNavigate, useParams } from "react-router";
import { cn } from "@/lib/cn";
import { useIsMobile } from "@/lib/use-media-query";

export type Secao = {
  chave: string;
  grupo: string;
  rotulo: string;
  descricao: string;
  icone: ReactNode;
  cor: string;
  conteudo: ReactNode;
  /** Seção que já desenha os próprios cartões (Agenda, Notificações…) vai sem a moldura. */
  semMoldura?: boolean;
};

/**
 * Página de configurações com seções: barra lateral agrupada no desktop; no celular, como os Ajustes
 * do iPhone — a lista de seções em `/base` e cada seção numa tela própria com "‹ voltar". A seção
 * vem da URL (`/base/:secao`) — link direto, voltar do navegador e tour caem na seção certa; seção
 * que não existe (ou que a pessoa não pode ver) volta pra primeira (no celular, pra lista).
 */
export function PaginaComSecoes({
  base,
  titulo,
  secoes,
  topo,
}: {
  base: string;
  titulo: string;
  secoes: Secao[];
  /** Acima das seções no desktop; no celular, só na lista (cada seção é uma tela própria). */
  topo?: ReactNode;
}) {
  const { secao } = useParams();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const ativa = secoes.find((s) => s.chave === secao);
  const grupos = [...new Set(secoes.map((s) => s.grupo))];
  if (!ativa && isMobile) return secao ? <Navigate to={base} replace /> : <ListaDeSecoes base={base} grupos={grupos} secoes={secoes} topo={topo} />;
  if (!ativa) return secoes[0] ? <Navigate to={`${base}/${secoes[0].chave}`} replace /> : null;
  const ir = (chave: string) => navigate(`${base}/${chave}`);

  return (
    <div className="mx-auto w-full max-w-[1400px] space-y-4 pb-10">
      {!isMobile && topo}
      {isMobile && (
        <Link to={base} className="inline-flex items-center gap-1 text-sm font-medium text-brand-700 dark:text-primary">
          <ChevronLeft className="h-4 w-4" />
          {titulo}
        </Link>
      )}

      <div className="flex gap-6">
        {!isMobile && (
          <nav data-tour="config-secoes" className="sticky top-20 w-60 shrink-0 self-start space-y-4">
            {grupos.map((g) => (
              <div key={g}>
                <p className="mb-1 px-3 text-[10px] font-semibold uppercase tracking-wider text-zinc-400 dark:text-muted-foreground/80">
                  {g}
                </p>
                {secoes
                  .filter((s) => s.grupo === g)
                  .map((s) => (
                    <button
                      key={s.chave}
                      type="button"
                      onClick={() => ir(s.chave)}
                      aria-current={s.chave === ativa.chave ? "page" : undefined}
                      className={cn(
                        "flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-left text-sm transition-colors",
                        s.chave === ativa.chave
                          ? "bg-white font-semibold text-zinc-900 shadow-sm ring-1 ring-zinc-200 dark:bg-card dark:text-foreground dark:ring-border"
                          : "text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900 dark:text-muted-foreground dark:hover:bg-muted dark:hover:text-foreground",
                      )}
                    >
                      <span style={{ color: s.cor }}>{s.icone}</span>
                      {s.rotulo}
                    </button>
                  ))}
              </div>
            ))}
          </nav>
        )}

        <div className="min-w-0 flex-1">
          {ativa.semMoldura ? (
            ativa.conteudo
          ) : (
            <div className="rounded-xl border border-zinc-200 bg-white shadow-sm dark:border-border dark:bg-card">
              <div className="flex items-center gap-3 border-b border-zinc-100 px-6 py-4 dark:border-border">
                <div
                  className="flex h-9 w-9 items-center justify-center rounded-lg"
                  style={{ backgroundColor: `${ativa.cor}1a`, color: ativa.cor }}
                >
                  {ativa.icone}
                </div>
                <div>
                  <h2 className="text-sm font-semibold text-zinc-900 dark:text-foreground">{ativa.rotulo}</h2>
                  <p className="text-xs text-zinc-500 dark:text-muted-foreground">{ativa.descricao}</p>
                </div>
              </div>
              {ativa.conteudo}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/** A lista do celular: grupos em cartões, cada seção uma linha com ícone colorido, nome, descrição e "›". */
function ListaDeSecoes({ base, grupos, secoes, topo }: { base: string; grupos: string[]; secoes: Secao[]; topo?: ReactNode }) {
  return (
    <div className="mx-auto w-full max-w-6xl space-y-5 pb-10">
      {topo}
      <div data-tour="config-secoes" className="space-y-5">
        {grupos.map((g) => (
          <section key={g}>
            <p className="mb-1.5 px-1 text-[11px] font-semibold uppercase tracking-wider text-zinc-400 dark:text-muted-foreground/80">{g}</p>
            <div className="divide-y divide-zinc-100 overflow-hidden rounded-xl border border-zinc-200 bg-white shadow-sm dark:divide-border dark:border-border dark:bg-card">
              {secoes
                .filter((s) => s.grupo === g)
                .map((s) => (
                  <Link key={s.chave} to={`${base}/${s.chave}`} className="flex min-h-14 items-center gap-3 px-3 py-2.5 active:bg-zinc-50 dark:active:bg-muted">
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg" style={{ backgroundColor: `${s.cor}1a`, color: s.cor }}>
                      {s.icone}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm font-medium text-zinc-900 dark:text-foreground">{s.rotulo}</span>
                      <span className="block truncate text-[12px] text-zinc-500 dark:text-muted-foreground">{s.descricao}</span>
                    </span>
                    <ChevronRight className="h-4 w-4 shrink-0 text-zinc-300" />
                  </Link>
                ))}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}
